import React, { useState, useEffect, useCallback, useRef } from 'react';
import { MdSlideshow } from 'react-icons/md';
import { useMediaInfinite } from '../api';
import { useQueryClient } from '@tanstack/react-query';
import { usePhotoSelection } from './PhotoGallery/hooks/usePhotoSelection';
import { usePhotoDownload } from './PhotoGallery/hooks/usePhotoDownload';
import { getThumbnailUrl } from './PhotoGallery/utils/photoHelpers';
import { useMemoryManagement } from './PhotoGallery/hooks/useMemoryManagement';
import PhotoGrid from './PhotoGallery/PhotoGrid';
import PhotoModal from './PhotoGallery/PhotoModal';
import SlideshowModal from './PhotoGallery/SlideshowModal';
import DownloadProgressOverlay from './PhotoGallery/DownloadProgressOverlay';
import './PhotoGallery.css';

const PhotoGallery = ({ selectionMode, setSelectionMode, refreshTrigger, showMediaFilter = true }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [modalAutoPlay, setModalAutoPlay] = useState(false);
  const [gridConfig, setGridConfig] = useState({ columnCount: 3, itemSize: 120 });
  const [slideshowOpen, setSlideshowOpen] = useState(false);
  const [slideshowStartIndex, setSlideshowStartIndex] = useState(0);
  const [filterType, setFilterType] = useState('all'); // 'all', 'pictures', 'videos'
  const slideshowAutoHideDelay = 3000; // 3 seconds default
  // Reduced for mobile optimization - fewer images in memory at once
  const photosPerPage = 20;
  // Floating actions visibility on scroll
  const [showFloatingActions, setShowFloatingActions] = useState(true);
  const lastScrollYRef = useRef(0);
  const hideTimeoutRef = useRef(null);

  // Query client for cache management
  const queryClient = useQueryClient();

  // Memory management for the entire gallery - optimized for 20 photos per page
  const { updateVisibleImages, cleanupOffscreenImages } = useMemoryManagement({
    bufferMultiplier: 2, // Moderate buffer for window scrolling
    cleanupInterval: 30000 // Cleanup every 30 seconds (more frequent with fewer photos)
  });

  // Map filterType to API type parameter
  const apiType = React.useMemo(() => {
    if (filterType === 'pictures') return 'image';
    if (filterType === 'videos') return 'video';
    return 'all';
  }, [filterType]);

  // Use React Query infinite query for media data with type filtering
  const {
    data: infiniteData,
    isLoading: photosLoading,
    error: photosError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch: refetchPhotos
  } = useMediaInfinite(photosPerPage, apiType);

  // Flatten the infinite data into a single array of media items
  const allMediaItems = React.useMemo(() =>
    infiniteData?.pages?.flatMap(page => page.items) || [],
    [infiniteData?.pages]
  );

  // Extract filenames for backward compatibility with existing code
  const allPhotos = React.useMemo(() =>
    allMediaItems.map(item => item.filename),
    [allMediaItems]
  );

  // Photos array (same as allPhotos since filtering is done server-side)
  const photos = allPhotos;

  // Use photo selection hook
  const {
    selectedPhotos,
    selectAll,
    isDraggingSelection,
    lastSelectedPhoto,
    clearSelections,
    togglePhotoSelection,
    toggleSelectAll,
    bindSelectionGesture
  } = usePhotoSelection(selectionMode, photos);

  // Use photo download hook
  const { downloadSelectedPhotos, downloadState } = usePhotoDownload(
    selectedPhotos,
    () => {
      // Callback when download is complete
      setSelectionMode(false);
      clearSelections();
    }
  );

  // Calculate grid configuration based on screen size
  const calculateGridConfig = useCallback(() => {
    const width = window.innerWidth;
    let columnCount = 3;
    let itemSize = 120;

    if (width >= 1024) {
      columnCount = 5;
      itemSize = Math.floor((width - 32) / columnCount); // Account for padding
    } else if (width >= 768) {
      columnCount = 4;
      itemSize = Math.floor((width - 24) / columnCount);
    } else if (width >= 640) {
      columnCount = 4;
      itemSize = Math.floor((width - 16) / columnCount);
    } else {
      columnCount = 3;
      itemSize = Math.floor((width - 16) / columnCount);
    }

    // Ensure minimum item size
    itemSize = Math.max(itemSize, 100);

    setGridConfig({
      columnCount,
      itemSize,
    });
  }, []);

  // Cache photos immediately when loaded from API (tightly coupled with API requests)
  const cachePhotosImmediately = useCallback((newPhotos) => {
    if (!newPhotos || newPhotos.length === 0) return;

    // Process photos in small batches to avoid overwhelming the device
    const batchSize = 5;
    for (let i = 0; i < newPhotos.length; i += batchSize) {
      const batch = newPhotos.slice(i, i + batchSize);

      // Slight delay between batches to prevent blocking
      setTimeout(() => {
        batch.forEach((filename) => {
          // Cache the thumbnail (what's shown in gallery)
          const thumbUrl = getThumbnailUrl(filename);

          // Send direct message to Service Worker to cache immediately
          if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
            navigator.serviceWorker.controller.postMessage({
              type: 'CACHE_PHOTO_IMMEDIATELY',
              url: thumbUrl
            });
          }
        });
      }, (i / batchSize) * 200); // 200ms delay between batches
    }
  }, []);

  // Track previously loaded photos to detect new ones from API
  const prevPhotosRef = useRef([]);

  useEffect(() => {
    const prevPhotos = prevPhotosRef.current;
    const newPhotos = allPhotos.filter(photo => !prevPhotos.includes(photo));

    if (newPhotos.length > 0) {
      // Small delay to ensure Service Worker is ready, then cache photos immediately when loaded from API
      setTimeout(() => {
        cachePhotosImmediately(newPhotos);
      }, 100);
    }

    prevPhotosRef.current = allPhotos;
  }, [allPhotos, cachePhotosImmediately]);


  // Handle closing modal
  const handleCloseModal = useCallback(() => {
    setModalOpen(false);
    setCurrentPhotoIndex(0);
    setModalAutoPlay(false); // Reset autoPlay state
  }, []);

  // Keyboard navigation for modal
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!modalOpen) return;

      switch (e.key) {
        case 'Escape':
          e.preventDefault();
          handleCloseModal();
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [modalOpen, handleCloseModal]);



  // Calculate grid configuration on mount and window resize
  useEffect(() => {
    calculateGridConfig();
    const handleResize = () => calculateGridConfig();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [calculateGridConfig]);

  // Periodic refresh for new photos (useful when multiple users are uploading)
  useEffect(() => {
    const interval = setInterval(() => {
      // Only refresh if the component is visible and not currently fetching
      if (document.visibilityState === 'visible' && !isFetchingNextPage) {
        queryClient.invalidateQueries({
          queryKey: ['media'],
          refetchType: 'none' // Don't refetch immediately, just mark as stale
        });
        // React Query will refetch when needed due to staleTime
      }
    }, 2 * 60 * 1000); // Check every 2 minutes

    return () => clearInterval(interval);
  }, [queryClient, isFetchingNextPage]);


  // Load more photos function using infinite query
  const loadMorePhotos = useCallback(() => {
    if (!hasNextPage || isFetchingNextPage) return;
    fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);


  // Handle authentication errors (401 = unauthorized, should logout)
  // Note: 403 from /media endpoint might also indicate auth failure, so handle both
  useEffect(() => {
    if (photosError?.response?.status === 401 || 
        (photosError?.response?.status === 403 && photosError?.response?.data?.includes?.('token'))) {
      // Clear invalid token and redirect
      console.error('Authentication error:', photosError?.response?.data);
      localStorage.removeItem('weddingToken');
      setTimeout(() => {
        window.location.href = '/';
      }, 2000);
    }
  }, [photosError]);

  // Track the last time we attempted to load more to prevent rapid fire requests
  const lastLoadAttemptRef = useRef(0);
  const MIN_LOAD_INTERVAL = 1000; // Minimum 1 second between load attempts

  // Scroll event listener for infinite scroll
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;

      // Floating actions visibility based on scroll direction
      // This should always run regardless of loading state
      const lastY = lastScrollYRef.current;
      if (scrollTop > lastY + 2) {
        // Scrolling down → hide actions
        if (showFloatingActions) setShowFloatingActions(false);
      } else if (scrollTop < lastY - 2) {
        // Scrolling up → show actions
        if (!showFloatingActions) setShowFloatingActions(true);
      }
      lastScrollYRef.current = scrollTop;

      // Update memory management for visible images
      // These should always run regardless of loading state
      updateVisibleImages();

      // Occasionally run cleanup to manage memory
      if (Math.random() < 0.1) {
        cleanupOffscreenImages();
      }

      // Early return for loading logic only
      if (!hasNextPage || isFetchingNextPage) return;

      const now = Date.now();
      if (now - lastLoadAttemptRef.current < MIN_LOAD_INTERVAL) {
        return; // Prevent rapid fire loading
      }

      // Check if there's actually content to scroll to
      const hasScrollableContent = documentHeight > windowHeight;

      if (hasScrollableContent) {
        // Load more when user is within 200px of the bottom
        if (scrollTop + windowHeight >= documentHeight - 200) {
          lastLoadAttemptRef.current = now;
          loadMorePhotos();
        }
      }
    };

    // Also check on initial load and window resize
    const checkAndLoad = () => {
      // Update memory management for visible images
      // These should always run regardless of loading state
      // Especially important on resize when viewport changes
      updateVisibleImages();

      // Early return for loading logic only
      if (!hasNextPage || isFetchingNextPage) return;

      const now = Date.now();
      if (now - lastLoadAttemptRef.current < MIN_LOAD_INTERVAL) {
        return; // Prevent rapid fire loading
      }

      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;

      // Auto-load if content fits in viewport
      // Allow loading even when photos.length is 0 (e.g., Videos tab with no videos loaded yet)
      if (documentHeight <= windowHeight && hasNextPage) {
        lastLoadAttemptRef.current = now;
        loadMorePhotos();
      }
    };

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', checkAndLoad);

    // Check on initial load with a delay
    const timeoutId = setTimeout(checkAndLoad, 500);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', checkAndLoad);
      clearTimeout(timeoutId);
    };
  }, [hasNextPage, isFetchingNextPage, loadMorePhotos, updateVisibleImages, cleanupOffscreenImages, photos.length, showFloatingActions]);




  // Service Worker handles photo caching automatically during normal viewing

  // Check cached photos for debugging (reduced logging for performance)
  const checkCachedPhotos = useCallback(async () => {
    if ('caches' in window && process.env.NODE_ENV === 'development') {
      try {
        const cache = await caches.open('wedding-photos-cache');
        await cache.keys();
      } catch (error) {
        // Silent fail in production to avoid performance impact
      }
    }
  }, []);

  // Check cached photos when photos load
  useEffect(() => {
    if (allPhotos.length > 0) {
      checkCachedPhotos();
    }
  }, [allPhotos, checkCachedPhotos]);

  // Handle refresh trigger from upload completion
  useEffect(() => {
    if (refreshTrigger > 0) {
      refetchPhotos();
    }
  }, [refreshTrigger, refetchPhotos]);

  // Handle photo click (selection or viewing)
  const handlePhotoClick = (filename) => {
    if (selectionMode) {
      togglePhotoSelection(filename);
    } else {
      handleOpenModal(filename);
    }
  };

  // Handle opening modal
  const handleOpenModal = (filename) => {
    // Use allPhotos for modal navigation (all media regardless of filter)
    // For better UX, we should use allMediaItems but for now keep compatibility
    const photoIndex = allPhotos.findIndex(photo => photo === filename);
    if (photoIndex !== -1) {
      setCurrentPhotoIndex(photoIndex);
      setModalOpen(true);
    }
  };


  // Handle modal slide changes
  const handleModalSlideChange = (newIndex) => {
    setCurrentPhotoIndex(newIndex);

    // Load more photos if we're near the end
    if (newIndex >= photos.length - 5 && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  };

  // Skeleton loading component
  const SkeletonLoader = () => {
    const skeletonItems = Array.from({ length: 12 }, (_, i) => i);

    return (
      <div className="w-full bg-gradient-to-b from-accent-20 to-accent-10">
        <div className="photo-grid"
          style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${gridConfig.columnCount}, 1fr)`,
            gap: '0.5rem',
            padding: '0.5rem'
          }}>
          {skeletonItems.map((index) => (
            <div key={index} className="aspect-square overflow-hidden rounded-lg bg-surface shadow-sm">
              <div className="w-full h-full bg-gradient-to-r from-primary-10 via-primary-20 to-primary-10 animate-pulse"></div>
            </div>
          ))}
        </div>
        <div className="flex items-center justify-center space-x-2 py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          <span className="text-secondary font-medium text-center">Loading your memories...</span>
        </div>
      </div>
    );
  };

  // Helper function to extract user-friendly error message
  const getErrorMessage = (error) => {
    if (!error) return 'An unknown error occurred';

    // Handle Axios errors with response
    if (error.response) {
      const status = error.response.status;
      const message = error.response.data || error.response.statusText || '';

      if (status === 401) {
        return 'Unauthorized. Please log in again.';
      } else if (status === 403 || status === 503) {
        return 'Access denied. Please check permissions.';
      } else if (status === 404) {
        return 'Photos not found. Please verify the configuration.';
      } else if (status === 429) {
        return 'Too many requests. Please try again later.';
      } else if (status === 500) {
        return 'Server error. Please try again later.';
      } else if (typeof message === 'string' && message) {
        return message;
      }
      
      return `Failed to load photos (Error ${status})`;
    }

    // Handle network errors
    if (error.request) {
      return 'Unable to connect to the server. Please check your internet connection.';
    }

    // Handle error message string
    if (typeof error.message === 'string' && error.message) {
      return error.message;
    }

    // Fallback for other error types
    return 'An error occurred while loading photos. Please try again.';
  };

  if (photosLoading) return <SkeletonLoader />;

  if (photosError) {
    const isAuthError = photosError?.response?.status === 401;
    const isNetworkError = !photosError?.response || photosError?.response?.status >= 500;
    const errorMessage = getErrorMessage(photosError);

    return (
      <div className="flex flex-col items-center justify-center py-16 px-4">
        <div className="text-center max-w-md">
          <div className="mb-6">
            {isAuthError ? (
              <svg className="mx-auto h-16 w-16 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m0 4h.01M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z" />
              </svg>
            ) : isNetworkError ? (
              <svg className="mx-auto h-16 w-16 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18.364 5.636l-12.728 12.728m0 0L12 12m-6.364 6.364L12 12m6.364-6.364L12 12" />
              </svg>
            ) : (
              <svg className="mx-auto h-16 w-16 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z" />
              </svg>
            )}
          </div>

          <h2 className="text-xl font-semibold text-secondary mb-3">
            {isAuthError ? 'Authentication Required' : isNetworkError ? 'Connection Issue' : 'Something went wrong'}
          </h2>

          <p className="text-secondary-70 mb-6 leading-relaxed">
            {errorMessage}
          </p>

          <div className="space-y-3">
            <button
              onClick={() => refetchPhotos()}
              className="w-full inline-flex items-center justify-center space-x-2 px-6 py-3 bg-primary rounded-lg hover:bg-primary-90 transition-colors shadow-sm" style={{color: 'var(--color-surface)'}}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              <span>Try Again</span>
            </button>

            {isAuthError && (
              <button
                onClick={() => window.location.href = '/'}
                className="w-full inline-flex items-center justify-center space-x-2 px-6 py-3 bg-surface text-primary border-2 border-primary rounded-lg hover:bg-primary-5 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
                </svg>
                <span>Sign In Again</span>
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }


  try {
    return (
      <div className="w-full bg-surface">
        {/* Download Progress Overlay */}
        <DownloadProgressOverlay downloadState={downloadState} />

        {/* Filter Buttons */}
        {showMediaFilter && (
          <div className="sticky top-0 bg-surface-90 px-4 pt-2">
            <div className="flex items-center justify-center gap-1.5">
              <button
                onClick={() => setFilterType('all')}
                className={`px-3 py-1 rounded-md text-xs font-medium transition-all duration-200 ${filterType === 'all'
                  ? 'bg-primary shadow-sm'
                  : 'bg-surface text-secondary border border-accent-30 hover:bg-primary-10'
                  }`}
                style={filterType === 'all' ? { color: 'var(--color-surface)' } : {}}
                aria-pressed={filterType === 'all'}
              >
                All
              </button>
              <button
                onClick={() => setFilterType('pictures')}
                className={`px-3 py-1 rounded-md text-xs font-medium transition-all duration-200 ${filterType === 'pictures'
                  ? 'bg-primary shadow-sm'
                  : 'bg-surface text-secondary border border-accent-30 hover:bg-primary-10'
                  }`}
                style={filterType === 'pictures' ? { color: 'var(--color-surface)' } : {}}
                aria-pressed={filterType === 'pictures'}
              >
                Photos
              </button>
              <button
                onClick={() => setFilterType('videos')}
                className={`px-3 py-1 rounded-md text-xs font-medium transition-all duration-200 ${filterType === 'videos'
                  ? 'bg-primary shadow-sm'
                  : 'bg-surface text-secondary border border-accent-30 hover:bg-primary-10'
                  }`}
                style={filterType === 'videos' ? { color: 'var(--color-surface)' } : {}}
                aria-pressed={filterType === 'videos'}
              >
                Videos
              </button>
            </div>
          </div>
        )}

        {/* Slideshow Button - Top Right Corner */}
        {!selectionMode && (
          <div className="absolute top-16 right-4 z-10">
            <button
              onClick={() => {
                setSlideshowStartIndex(0);
                setSlideshowOpen(true);
              }}
              className="flex items-center justify-center bg-surface-90 backdrop-blur-sm p-2 rounded-full border border-accent shadow-sm transition-all duration-200 hover:bg-surface hover:scale-105 active:scale-95 cursor-pointer"
              title="Start slideshow"
              aria-label="Start automatic slideshow"
            >
              <MdSlideshow className="w-8 h-4 text-primary" />
            </button>
          </div>
        )}

        {/* Floating Action Buttons - Upload and Select */}
        {!selectionMode && (
          <div
            className={`fixed bottom-6 right-4 z-20 flex flex-col items-end gap-3 transition-opacity duration-150 ${showFloatingActions ? 'opacity-100' : 'opacity-20'}`}
            onPointerDown={() => {
              // Reappear on tap and auto-hide after a delay when idle
              setShowFloatingActions(true);
              if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
              hideTimeoutRef.current = setTimeout(() => setShowFloatingActions(false), 1500);
            }}
          >
            {/* Upload FAB */}
            <div className="relative group">
              <button
                onClick={() => { window.location.href = '/dashboard/upload'; }}
                className="w-14 h-14 rounded-full shadow-lg bg-primary hover:bg-accent-90 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-secondary-50 focus:ring-offset-2 flex items-center justify-center" style={{color: 'var(--color-surface)'}}
                aria-label="Upload photos and videos"
                title="Upload photos and videos"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v8" />
                </svg>
              </button>
              <div className="absolute bottom-full right-0 mb-3 px-3 py-2 bg-secondary text-surface text-xs rounded-lg shadow-lg opacity-0 group-active:opacity-100 sm:group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-50 pointer-events-none">
                Upload
                <div className="absolute top-full right-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-secondary"></div>
              </div>
            </div>

            {/* Select FAB */}
            <div className="relative group">
              <button
                onClick={() => setSelectionMode(true)}
                className="mobile-select-button w-14 h-14 rounded-full shadow-lg bg-primary hover:bg-primary-90 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary-50 focus:ring-offset-2 flex items-center justify-center" style={{color: 'var(--color-surface)'}}
                aria-pressed={false}
                aria-label="Enter selection mode to choose multiple photos for download"
                title="Select multiple photos to download or share"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </button>
              <div className="absolute bottom-full right-0 mb-3 px-3 py-2 bg-secondary text-surface text-xs rounded-lg shadow-lg opacity-0 group-active:opacity-100 sm:group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-50 pointer-events-none max-w-[200px] sm:max-w-none">
                Select photos
                <div className="absolute top-full right-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-secondary"></div>
              </div>
            </div>
          </div>
        )}
        {/* Selection Mode Controls */}
        {selectionMode && (
          <div className="sticky top-0 z-40 bg-neutral-30 backdrop-blur-md border-1 border-accent px-3 sm:px-4 py-2 sm:py-2 mt-2 rounded-lg shadow-sm" role="region" aria-label="Photo selection controls">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-2 sm:space-y-0">
              {/* Header with selection info */}
              <div className="flex items-center justify-between sm:justify-start space-x-3">
                <div className="flex items-center space-x-3">
                  <div className="w-7 h-7 bg-primary rounded-full flex items-center justify-center">
                    <svg className="w-4 h-4 text-surface" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <h2 className="font-semibold text-sm text-secondary">Selection Mode</h2>
                </div>
                <div className="inline-flex items-center justify-center bg-neutral px-3 h-7 rounded-full border border-accent-30 min-w-[150px] text-center">
                  <span className="font-medium text-xs text-secondary leading-none" aria-live="polite" aria-atomic="true">
                    {selectedPhotos.size} of {photos.length} selected
                  </span>
                </div>
              </div>

              {/* Action buttons */}
              <div className="flex items-center justify-between sm:justify-end space-x-3">
                <button
                  onClick={toggleSelectAll}
                  className={`flex items-center justify-center space-x-2 px-3 py-2 w-32 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 min-h-[36px] border ${selectAll
                    ? 'bg-secondary text-surface border-secondary hover:bg-secondary-90 focus:ring-secondary-50 focus:ring-offset-accent'
                    : 'bg-primary text-surface border-accent hover:bg-accent-90 focus:ring-accent-50 focus:ring-offset-accent'
                    }`}
                  aria-label={selectAll ? `Deselect all ${photos.length} photos` : `Select all ${photos.length} photos`}
                >
                  <span className="font-medium text-sm">{selectAll ? 'Deselect All' : 'Select All'}</span>
                </button>

                <button
                  onClick={downloadSelectedPhotos}
                  disabled={selectedPhotos.size === 0 || downloadState?.isDownloading}
                  className="flex items-center justify-center space-x-2 px-3 py-2  bg-primary text-surface rounded-lg hover:bg-primary-90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium focus:outline-none focus:ring-2 focus:ring-primary-50 focus:ring-offset-2 focus:ring-offset-accent min-h-[36px] flex-1 sm:flex-none border border-primary"
                  aria-label={`Download ${selectedPhotos.size} selected photos`}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5 5-5M12 15V3" />
                  </svg>
                  <span className="text-sm">Save ({selectedPhotos.size})</span>
                </button>

                <button
                  onClick={() => {
                    setSelectionMode(false);
                    clearSelections();
                  }}
                  className="flex items-center justify-center px-3 py-2 bg-neutral text-secondary rounded-lg hover:bg-neutral-90 transition-colors font-medium focus:outline-none focus:ring-2 focus:ring-neutral-50 focus:ring-offset-2 focus:ring-offset-accent min-h-[36px] border border-accent leading-none"
                  aria-label="Exit selection mode"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Photo Grid */}
        <PhotoGrid
          photos={photos}
          selectionMode={selectionMode}
          selectedPhotos={selectedPhotos}
          isDraggingSelection={isDraggingSelection}
          lastSelectedPhoto={lastSelectedPhoto}
          onPhotoClick={handlePhotoClick}
          bindSelectionGesture={bindSelectionGesture}
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          filterType={filterType}
        />

        {/* Empty state when no photos match filter and not loading */}
        {!isFetchingNextPage && photos.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16 px-4">
            <div className="text-center max-w-md">
              <svg className="mx-auto h-24 w-24 text-primary-40 mb-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <h2 className="text-xl font-semibold text-secondary mb-2">
                {filterType === 'videos' ? 'No Videos Yet' : filterType === 'pictures' ? 'No Photos Yet' : 'No Photos Yet'}
              </h2>
              <p className="text-secondary-60 mb-6 leading-relaxed">
                The wedding gallery is empty. {filterType === 'videos' ? 'Videos' : 'Photos'} will appear here once they're uploaded.
              </p>
              <button
                onClick={() => window.location.href = '/dashboard/upload'}
                className="inline-flex items-center space-x-2 px-6 py-3 bg-primary rounded-lg hover:bg-primary-90 transition-colors shadow-sm" style={{color: 'var(--color-surface)'}}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                <span>Upload First {filterType === 'videos' ? 'Videos' : 'Photos'}</span>
              </button>
            </div>
          </div>
        )}

        {/* Load More Button - Minimal and compact */}
        {hasNextPage && !isFetchingNextPage && photos.length > 0 && (
          <div className="flex items-center justify-center py-6 px-4">
            <button
              onClick={loadMorePhotos}
              className="group flex items-center space-x-1.5 px-4 py-2 text-primary hover:text-primary-80 transition-colors text-sm font-medium"
              aria-label="Load more photos"
            >
              <span>Load More</span>
              <svg className="w-4 h-4 transition-transform group-hover:translate-y-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </div>
        )}

        {/* Lazy Loading Indicator - Only show when actively fetching */}
        {isFetchingNextPage && (
          <div className="flex flex-col items-center justify-center py-12 px-4">
            <div className="flex flex-col items-center space-y-3">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-accent border-t-primary"></div>
              <span className="text-secondary-70 text-sm">Loading your memories...</span>
            </div>
          </div>
        )}

        {/* End of Gallery Indicator */}
        {!hasNextPage && photos.length > 0 && (
          <div className="flex flex-col items-center justify-center py-12 px-4">
            <div className="text-center">
              <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-primary-10 flex items-center justify-center">
                <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <p className="text-secondary-70 font-medium mb-1">All caught up!</p>
              <p className="text-secondary-50 text-sm">
                {photos.length} {filterType === 'videos' ? 'videos' : filterType === 'pictures' ? 'photos' : 'items'} loaded
              </p>
            </div>
          </div>
        )}



        {/* Photo Modal */}
        {modalOpen && (
          <PhotoModal
            isOpen={modalOpen}
            photos={allPhotos}
            currentIndex={currentPhotoIndex}
            onClose={handleCloseModal}
            onSlideChange={handleModalSlideChange}
            hasNextPage={hasNextPage}
            fetchNextPage={fetchNextPage}
            autoPlay={modalAutoPlay}
            totalPhotos={allPhotos.length}
          />
        )}

        {/* Slideshow Modal */}
        {slideshowOpen && (
          <SlideshowModal
            isOpen={slideshowOpen}
            photos={allPhotos}
            startIndex={slideshowStartIndex}
            onClose={() => setSlideshowOpen(false)}
            autoHideDelay={slideshowAutoHideDelay}
            hasNextPage={hasNextPage}
            fetchNextPage={fetchNextPage}
          />
        )}
      </div>
    );
  } catch (error) {
    console.error('Error in PhotoGallery render:', error);
    return (
      <div className="w-full bg-gradient-to-b from-accent-20 to-accent-10">
        <div className="flex items-center justify-center py-16 px-4">
          <div className="text-center max-w-md">
            <div className="text-red-500 mb-4">Error loading photo gallery</div>
            <div className="text-sm text-gray-600">
              Please refresh the page to try again.
            </div>
          </div>
        </div>
      </div>
    );
  }
};

export default PhotoGallery;

